import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, Form, InputNumber, Select, Tag } from 'antd';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';

import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { RedirectLink } from 'app/components/commons/RedirectLink/RedirectLink';
import { CoverCellRoom } from 'app/components/lists/cells';
import { useGetExperiencesByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelExperiences';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { EXPERIENCE_AVAILABLE_DAY_LIMIT } from 'app/typings/core/admin/experiences/enum/experienceAvailableDayLimit';
import { EXPERIENCE_STATUS } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import type { PackExperienceResponse } from 'app/typings/core/admin/packs/pack';

import { ExperiencePrice } from './ExperiencePrice';
import { getAdultPriceRange } from './prices';

type SortableExperienceProps = {
  packExperience: PackExperienceResponse;
  index: number;
  isEditMode: boolean;
};

const SortableExperience = ({
  packExperience,
  index,
  isEditMode,
}: SortableExperienceProps) => {
  const form = Form.useFormInstance();
  const { attributes, setNodeRef, listeners, transform, transition } =
    useSortable({
      id: packExperience.experienceId,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { data: experienceCategories } = useExperiencesCategories();

  const hotelId = Form.useWatch('hotelId', form);
  const isDaytime = Form.useWatch('isDaytime', form);

  const { data: hotelExperiences } = useGetExperiencesByHotelId(hotelId, {
    withStaycationExperiences: true,
  });

  const experience = hotelExperiences?.experiences.find(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (experience) => experience.id === packExperience.experienceId
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    const current = form.getFieldValue('packExperiences') || [];

    const updated = current.filter(
      (exp: PackExperienceResponse) =>
        exp.experienceId !== packExperience.experienceId
    );

    form.setFieldsValue({ packExperiences: updated });
    form.validateFields();
  };

  const supportsPerNight =
    experience?.availableDayLimits?.includes(
      EXPERIENCE_AVAILABLE_DAY_LIMIT.MID_STAY
    ) &&
    (experience?.availableDayLimits?.includes(
      EXPERIENCE_AVAILABLE_DAY_LIMIT.CHECK_IN
    ) ||
      experience?.availableDayLimits?.includes(
        EXPERIENCE_AVAILABLE_DAY_LIMIT.CHECK_OUT
      ));

  const experienceName = computeExperienceName({
    category: experienceCategories?.find(
      (cat) => cat.id === experience?.categoryId
    ),
    quantity: null,
    brand: experience?.brand || null,
    duration: experience?.duration || null,
    durationDescription: experience?.durationDescription || null,
    time: experience?.time || null,
    locations: experience?.location || null,
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="er-item-wrapper"
    >
      <div className="er-item">
        <div className="er-item-main">
          <div className="er-item-left">
            <CoverCellRoom
              category={experienceName}
              entity={{
                coverPicture: packExperience.isIncluded
                  ? experience?.coverPicture
                  : experience?.emoji,
                name:
                  packExperience.isOptional && packExperience.isIncluded
                    ? 'Optional'
                    : undefined,
              }}
            />
          </div>

          <div className="er-item-right">
            <ExperiencePrice
              priceRange={getAdultPriceRange(experience, !!isDaytime)}
            />

            {packExperience.isIncluded ? (
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const quantity =
                    getFieldValue(['packExperiences', index, 'quantity']) ??
                    packExperience.quantity ??
                    1;
                  const isPerPerson =
                    getFieldValue(['packExperiences', index, 'isPerPerson']) ??
                    packExperience.isPerPerson;
                  const isPerNight =
                    getFieldValue(['packExperiences', index, 'isPerNight']) ??
                    packExperience.isPerNight;

                  return (
                    <div className="er-value er-tags">
                      <Tag>{quantity}</Tag>
                      <Tag>{isPerPerson ? 'per pers.' : 'unit'}</Tag>
                      {!isDaytime && (
                        <Tag>{isPerNight ? 'per night' : 'per stay'}</Tag>
                      )}
                    </div>
                  );
                }}
              </Form.Item>
            ) : (
              <span />
            )}

            <Form.Item name={['packExperiences', index, 'isIncluded']}>
              <Select
                className="er-select"
                disabled={!isEditMode}
                options={[
                  { label: 'Included', value: true },
                  { label: 'Extra', value: false },
                ]}
                onChange={(value) => {
                  const current: PackExperienceResponse[] =
                    form.getFieldValue('packExperiences') || [];

                  const updated = current.map((exp: PackExperienceResponse) =>
                    exp.experienceId === packExperience.experienceId
                      ? {
                          ...exp,
                          isIncluded: value,
                          isOptional: value ? exp.isOptional : true,
                          discount: value ? exp.discount : undefined,
                          priority: Infinity,
                        }
                      : exp
                  );

                  const included = updated
                    .filter((e) => e.isIncluded)
                    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                    .map((e, idx) => ({ ...e, priority: idx }));

                  const extras = updated
                    .filter((e) => !e.isIncluded)
                    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                    .map((e, idx) => ({
                      ...e,
                      priority: included.length + idx,
                    }));

                  form.setFieldsValue({
                    packExperiences: [...included, ...extras],
                  });
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Form.Item>

            <div className="er-status">
              <span
                className={`er-dot ${
                  experience?.status === EXPERIENCE_STATUS.PUBLISHED
                    ? 'published'
                    : 'unpublished'
                }`}
              />
              {experience?.status === EXPERIENCE_STATUS.PUBLISHED
                ? 'Published'
                : 'Unpublished'}
            </div>
            <div className="er-item-buttons">
              <div className="er-buttons-actions">
                {isEditMode ? (
                  <DeleteOutlined onClick={handleDelete} />
                ) : (
                  <span />
                )}
                <RedirectLink
                  href={`/activities/${packExperience.experienceId}`}
                />
              </div>
              {isEditMode ? (
                <HolderOutlined className="er-drag-handle" {...listeners} />
              ) : (
                <span className="er-drag-handle-placeholder" />
              )}
            </div>
          </div>
        </div>

        {packExperience.isIncluded && (
          <div
            className="er-item-expanded"
            style={!isEditMode ? { display: 'none' } : undefined}
          >
            <FormLineLayout label="Price description" isRequired>
              <div className="experience-input-group">
                <Form.Item
                  name={['packExperiences', index, 'quantity']}
                  getValueProps={(value) => ({ value: value ?? 1 })}
                  normalize={(value) => value ?? 1}
                  noStyle
                >
                  <InputNumber width={48} disabled={!isEditMode} min={1} />
                </Form.Item>

                <Form.Item
                  name={['packExperiences', index, 'isPerPerson']}
                  getValueProps={(value) => ({
                    value: value ? 'perPers' : 'unit',
                  })}
                  normalize={(value) => value === 'perPers'}
                  noStyle
                >
                  <Select
                    options={[
                      { label: 'per pers.', value: 'perPers' },
                      { label: 'unit', value: 'unit' },
                    ]}
                    disabled={!isEditMode}
                    style={{ width: 120 }}
                  />
                </Form.Item>

                {!isDaytime && (
                  <Form.Item
                    name={['packExperiences', index, 'isPerNight']}
                    getValueProps={(value) => ({
                      value: value ? 'perNight' : 'perStay',
                    })}
                    normalize={(value) => value === 'perNight'}
                    noStyle
                  >
                    <Select
                      options={[
                        { label: 'per stay', value: 'perStay' },
                        {
                          label: 'per night',
                          value: 'perNight',
                          disabled: !supportsPerNight,
                        },
                      ]}
                      disabled={!isEditMode}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                )}
              </div>
            </FormLineLayout>

            <FormLineLayout label="Must-have" labelOnTop>
              <Form.Item
                name={['packExperiences', index, 'isOptional']}
                valuePropName="checked"
                getValueProps={(value) => ({ checked: !value })}
                normalize={(checked) => !checked}
              >
                <Checkbox disabled={!isEditMode}>
                  When a must-have activity is unavailable, the package is
                  removed from sale.
                </Checkbox>
              </Form.Item>
            </FormLineLayout>
          </div>
        )}
      </div>
    </div>
  );
};

type SortableExperienceItem = {
  exp: PackExperienceResponse;
  index: number;
};

type SortableExperiencesListProps = {
  experiences: SortableExperienceItem[];
  isEditMode: boolean;
  onSortEnd: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
};

export const SortableExperiencesList = ({
  experiences,
  isEditMode,
  onSortEnd,
}: SortableExperiencesListProps) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = experiences.findIndex(
      ({ exp }) => exp.experienceId === active.id
    );
    const newIndex = experiences.findIndex(
      ({ exp }) => exp.experienceId === over.id
    );

    onSortEnd({ oldIndex, newIndex });
  };

  const ids = useMemo(
    () => experiences.map(({ exp }) => exp.experienceId),
    [experiences]
  );

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {experiences.map(({ exp, index }) => (
          <SortableExperience
            key={exp.experienceId}
            packExperience={exp}
            index={index}
            isEditMode={isEditMode}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
